import { authClient, api } from "@repo/db"
import { Wallpaper } from "@repo/ui"
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router"
import { useAction, useMutation } from "convex/react"
import { useEffect, useRef, useState } from "react"

import { convexClient } from "@/lib/convex"

export const Route = createFileRoute("/demo")({
  beforeLoad: async () => {
    const session = await authClient.getSession()
    if (!session.data) {
      throw redirect({ to: "/login", search: { redirect: "/demo" } })
    }
  },
  component: DemoRoute,
})

function DemoRoute() {
  const navigate = useNavigate()
  const provisionDemo = useMutation(api.demo.provision.provisionDemo)
  const seedDemoInventory = useAction(api.demo.provision.seedDemoInventory)
  const [status, setStatus] = useState("Preparing your demo...")
  const [error, setError] = useState<string | null>(null)
  const startedRef = useRef(false)

  // EXCEPTION TO USEEFFECT RULE:
  // Auto-executing route — needs to run sign-up + provision sequence once on mount,
  // then react to Convex auth state readiness before calling mutations.
  useEffect(() => {
    if (startedRef.current) return
    startedRef.current = true
    void runDemoSetup()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function runDemoSetup() {
    try {
      const id = crypto.randomUUID().slice(0, 8)
      const slug = `demo-${id}`

      // 1. Wait for the real signed-in account to be visible to Convex.
      setStatus("Authenticating...")
      const currentUser = await waitForConvexAuth()

      if (currentUser.isDemo && currentUser.dealership?.slug) {
        window.location.href = `/${currentUser.dealership.slug}/seo`
        return
      }

      // 2. Create BA organization owned by the current user.
      setStatus("Setting up dealership...")
      const orgResult = await authClient.organization.create({
        name: "Sunset Motors (Demo)",
        slug,
      })
      if (!orgResult.data?.id) {
        throw new Error("Failed to create demo organization")
      }

      // 3. Set active org
      await authClient.organization.setActive({
        organizationId: orgResult.data.id,
      })

      // 4. Provision demo data
      setStatus("Generating demo data...")
      const result = await provisionDemo({
        betterAuthOrgId: orgResult.data.id,
        slug,
      })

      // 5. Import demo inventory
      if (!result.storeId) {
        throw new Error("Demo store was not created")
      }
      setStatus("Importing demo inventory (this may take a little bit)...")
      await seedDemoInventory({
        storeId: result.storeId,
      })

      // 6. Navigate to demo dealership
      setStatus("Redirecting...")
      window.location.href = `/${result.slug}/seo`
    } catch (err) {
      console.error("Demo setup failed:", err)
      setError(err instanceof Error ? err.message : "Demo setup failed")
    }
  }

  if (error) {
    return (
      <div className="dark">
        <Wallpaper color="blue" className="min-h-svh flex items-center justify-center">
          <div className="flex flex-col items-center gap-4 text-center max-w-sm">
            <p className="text-sm font-medium text-white/90">Demo setup failed</p>
            <p className="text-xs text-white/60">{error}</p>
            <button
              onClick={() => navigate({ to: "/login", search: { redirect: undefined } })}
              className="text-sm text-primary underline underline-offset-2 hover:opacity-80"
            >
              Go to login
            </button>
          </div>
        </Wallpaper>
      </div>
    )
  }

  return (
    <div className="dark">
      <Wallpaper color="blue" className="min-h-svh flex items-center justify-center">
        <div className="flex flex-col items-center gap-10">
          <div className="relative flex items-center justify-center">
            <div className="absolute size-12 rounded-full border dark:border-white/25 border-black/25 animate-[loader-ring_2.4s_ease-in-out_infinite]" />
          </div>
          <p className="font-display text-sm font-semibold tracking-tight text-white/60">
            {status}
          </p>
        </div>
      </Wallpaper>
    </div>
  )
}

type CurrentUser = Awaited<ReturnType<typeof convexClient.query<typeof api.auth.getCurrentUser>>>

function waitForConvexAuth(timeoutMs = 10_000): Promise<NonNullable<CurrentUser>> {
  return new Promise((resolve, reject) => {
    const start = Date.now()
    const check = () => {
      // Check if we have a valid Convex user
      convexClient
        .query(api.auth.getCurrentUser)
        .then((user) => {
          if (user) {
            resolve(user)
          } else if (Date.now() - start > timeoutMs) {
            reject(new Error("Timed out waiting for authentication"))
          } else {
            setTimeout(check, 200)
          }
        })
        .catch(() => {
          if (Date.now() - start > timeoutMs) {
            reject(new Error("Timed out waiting for authentication"))
          } else {
            setTimeout(check, 200)
          }
        })
    }
    check()
  })
}
